import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";
import PageShell from "../layout/PageShell";

type SystemLog = {
  id: string;
  createdAt: number | null;
  event: string;
  success: boolean;
  email: string;
  uid: string;
  ip: string;
  ipv4: string;
  ipv6: string;
  ipChain: string;
  userAgent: string;
  message: string;
  country: string;
  countryCode: string;
  region: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
  isp: string;
  timezone: string;
};

function formatWhen(value: number | null) {
  if (!value) return "";
  return new Date(value).toLocaleString();
}

function shownIp(row: SystemLog) {
  return row.ipv4 || row.ip || row.ipv6 || "";
}

function place(row: SystemLog) {
  return [row.city, row.region, row.country].filter(Boolean).join(", ");
}

function DetailLine({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Box>
      <Text fontSize="xs" color="gray.500">{label}</Text>
      <Text whiteSpace="pre-wrap">{value}</Text>
    </Box>
  );
}

const SystemLogPage: React.FC = () => {
  const toast = useToast();
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"selected" | "older" | "all" | null>(null);
  const [openLog, setOpenLog] = useState<SystemLog | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listAdminSystemLogs = httpsCallable<void, { logs: SystemLog[] }>(functions, "listAdminSystemLogs");
      const result = await listAdminSystemLogs();
      const next = result.data.logs || [];
      setLogs(next);
      setSelected((current) => current.filter((id) => next.some((row) => row.id === id)));
    } catch (error: any) {
      toast({
        title: "Could not load the system log",
        description: error.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const allChecked = logs.length > 0 && selected.length === logs.length;

  const toggleAll = () => {
    setConfirmMode(null);
    setSelected(allChecked ? [] : logs.map((row) => row.id));
  };

  const toggleOne = (id: string) => {
    setConfirmMode(null);
    setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const handleClear = async (mode: "selected" | "older" | "all") => {
    if (confirmMode !== mode) {
      setConfirmMode(mode);
      return;
    }
    if (mode === "selected" && selected.length === 0) return;
    setClearing(true);
    try {
      const clearAdminSystemLogs = httpsCallable<
        { mode: "selected" | "older" | "all"; ids?: string[]; days?: number },
        { deleted: number }
      >(functions, "clearAdminSystemLogs");
      const result = await clearAdminSystemLogs({
        mode,
        ids: mode === "selected" ? selected : undefined,
        days: mode === "older" ? 30 : undefined,
      });
      setConfirmMode(null);
      setSelected([]);
      await load();
      toast({
        title: "Entries removed",
        description: `${result.data.deleted} log ${result.data.deleted === 1 ? "entry" : "entries"} removed.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Could not clear those entries",
        description: error.message || "Try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setClearing(false);
    }
  };

  return (
    <PageShell
      title="System Log"
      description="Sign-in, sign-out, password, and authenticator activity."
      fluid
      actions={
        <>
          <Button variant="outline" mr={3} onClick={load} isLoading={loading}>
            Refresh
          </Button>
          <Button
            variant="outline"
            mr={3}
            onClick={() => handleClear("selected")}
            isLoading={clearing && confirmMode === "selected"}
            isDisabled={selected.length === 0 || (clearing && confirmMode !== "selected")}
          >
            {confirmMode === "selected" ? `Confirm clear ${selected.length}` : `Clear selected (${selected.length})`}
          </Button>
          <Button
            variant="outline"
            mr={3}
            onClick={() => handleClear("older")}
            isLoading={clearing && confirmMode === "older"}
            isDisabled={clearing && confirmMode !== "older"}
          >
            {confirmMode === "older" ? "Confirm clear older than 30 days" : "Clear older than 30 days"}
          </Button>
          <Button
            colorScheme={confirmMode === "all" ? "red" : "gray"}
            onClick={() => handleClear("all")}
            isLoading={clearing && confirmMode === "all"}
            isDisabled={clearing && confirmMode !== "all"}
          >
            {confirmMode === "all" ? "Confirm clear all" : "Clear all"}
          </Button>
        </>
      }
    >
      <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th w="40px">
                <Checkbox
                  isChecked={allChecked}
                  isIndeterminate={selected.length > 0 && !allChecked}
                  onChange={toggleAll}
                  aria-label="Select all entries"
                />
              </Th>
              <Th>Time</Th>
              <Th>Event</Th>
              <Th>Result</Th>
              <Th>Email</Th>
              <Th>IP</Th>
              <Th>Location</Th>
              <Th>Network</Th>
              <Th>Detail</Th>
            </Tr>
          </Thead>
          <Tbody>
            {logs.length === 0 && !loading ? (
              <Tr>
                <Td colSpan={9}>
                  <Text color="gray.600" py={4}>No activity yet.</Text>
                </Td>
              </Tr>
            ) : logs.map((row) => (
              <Tr
                key={row.id}
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
                onClick={() => setOpenLog(row)}
              >
                <Td onClick={(event) => event.stopPropagation()}>
                  <Checkbox
                    isChecked={selected.includes(row.id)}
                    onChange={() => toggleOne(row.id)}
                    aria-label={`Select ${row.event}`}
                  />
                </Td>
                <Td whiteSpace="nowrap">{formatWhen(row.createdAt)}</Td>
                <Td whiteSpace="nowrap">{row.event}</Td>
                <Td>
                  <Badge colorScheme={row.success ? "green" : "red"}>{row.success ? "Success" : "Failed"}</Badge>
                </Td>
                <Td>{row.email}</Td>
                <Td whiteSpace="nowrap">{shownIp(row)}</Td>
                <Td>
                  <Text>{place(row) || "Unknown"}</Text>
                </Td>
                <Td>{row.isp}</Td>
                <Td minW="240px">
                  <Text noOfLines={2}>{row.message}</Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Drawer isOpen={!!openLog} placement="right" size="md" onClose={() => setOpenLog(null)}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{openLog ? openLog.event : "Log entry"}</DrawerHeader>
          <DrawerBody>
            {openLog ? (
              <VStack align="stretch" spacing={4} pb={8}>
                <DetailLine label="Time" value={formatWhen(openLog.createdAt)} />
                <DetailLine label="Result" value={openLog.success ? "Success" : "Failed"} />
                <DetailLine label="Email" value={openLog.email} />
                <DetailLine label="User id" value={openLog.uid} />
                <DetailLine label="IPv4" value={openLog.ipv4 || (shownIp(openLog).indexOf(":") === -1 ? shownIp(openLog) : "")} />
                <DetailLine label="IPv6" value={openLog.ipv6 || (shownIp(openLog).indexOf(":") !== -1 ? shownIp(openLog) : "")} />
                <DetailLine label="Addresses seen" value={openLog.ipChain} />
                <DetailLine label="Location" value={place(openLog)} />
                <DetailLine label="Country code" value={openLog.countryCode} />
                <DetailLine
                  label="Coordinates"
                  value={openLog.latitude != null && openLog.longitude != null ? `${openLog.latitude}, ${openLog.longitude}` : ""}
                />
                <DetailLine label="Timezone" value={openLog.timezone} />
                <DetailLine label="Network" value={openLog.isp} />
                <DetailLine label="Detail" value={openLog.message} />
                <DetailLine label="Browser" value={openLog.userAgent} />
              </VStack>
            ) : null}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </PageShell>
  );
};

export default SystemLogPage;
